import { useCallback, useState } from 'react'
import { Box, Chip, Link, MenuItem } from '@mui/material'
import { humanizeServiceName } from '@common/helpers/atlasAI'
import { IApprovalPreview, IApprovalSummary } from '@common/interfaces/atlasai'
import Button from '../../Common/Button/Button'
import Card from '../../Common/Card/Card'
import Icon from '../../Common/Icon'
import IconButton from '../../Common/IconButton'
import Select from '../../Common/Select/Select'
import Text from '../../Common/Text'
import TextField from '../../Common/TextField/TextField'
import styles from './PendingApprovals.module.scss'

const renderRawParams = (params: Record<string, unknown>) => {
  const entries = Object.entries(params)
  if (entries.length === 0) return null
  return (
    <Box className={styles.paramsBlock}>
      {entries.map(([key, value]) => (
        <Box key={key} className={styles.paramRow}>
          <Text
            variant="bodySm"
            tone="secondary"
            strong
            component="span"
            casing="none"
            className={styles.paramKey}
          >
            {key}
          </Text>
          <Text variant="bodySm" component="span" casing="none" className={styles.paramValue}>
            {typeof value === 'string' ? value : JSON.stringify(value)}
          </Text>
        </Box>
      ))}
    </Box>
  )
}

type PreviewField = NonNullable<IApprovalPreview['fields']>[number]

// Raw param value backing an editable field's input (what gets sent on accept).
const fieldRawValue = (
  param: string,
  params: Record<string, unknown>,
  edits: Record<string, string>,
): string => {
  const raw = edits[param] ?? params[param]
  if (typeof raw === 'string') return raw
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw)
  return ''
}

// What a row shows when NOT editing: the user's edit (mapped to its option label
// for selects) if they changed it, otherwise the server-provided display value.
const fieldDisplayValue = (field: PreviewField, edits: Record<string, string>): string => {
  const param = field.edit?.param
  if (!param || edits[param] === undefined) return field.value
  const edited = edits[param]
  if (field.edit?.type === 'select') {
    const opt = field.edit.options?.find((o) => o.value === edited)
    return opt ? (opt.label ?? opt.value) : edited
  }
  return edited
}

type FieldRowProps = {
  field: PreviewField
  params: Record<string, unknown>
  edits: Record<string, string>
  isEditing: boolean
  disabled: boolean
  onToggle: (param: string) => void
  onEdit: (param: string, value: string) => void
  onRevert: (param: string) => void
}

// A single preview row. Editable rows show a pencil that swaps the value for an
// inline input (check closes it; edits persist either way), plus an undo to
// revert a touched field to the original value. The value itself can also be a
// Salesforce/CRM link.
const FieldRow = ({
  field,
  params,
  edits,
  isEditing,
  disabled,
  onToggle,
  onEdit,
  onRevert,
}: FieldRowProps) => {
  const edit = field.edit
  const isEdited = !!edit && edits[edit.param] !== undefined
  return (
    <Box className={styles.previewFieldRow}>
      <Text
        variant="bodySm"
        tone="secondary"
        strong
        component="span"
        casing="none"
        className={styles.previewFieldLabel}
      >
        {field.label}
      </Text>
      {edit && isEditing ? (
        <>
          {edit.type === 'select' ? (
            <Select
              value={fieldRawValue(edit.param, params, edits)}
              disabled={disabled}
              onChange={(e) => onEdit(edit.param, e.target.value as string)}
              className={styles.previewFieldInput}
            >
              {(edit.options ?? []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label ?? opt.value}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <TextField
              useFinalForm={false}
              value={fieldRawValue(edit.param, params, edits)}
              disabled={disabled}
              onChange={(e) => onEdit(edit.param, e.target.value)}
              className={styles.previewFieldInput}
            />
          )}
          <IconButton
            title="Done editing"
            size="small"
            aria-label={`Done editing ${field.label}`}
            disabled={disabled}
            onClick={() => onToggle(edit.param)}
            className={styles.editIcon}
          >
            <Icon name="check" size="sm" />
          </IconButton>
        </>
      ) : (
        <Box className={styles.previewFieldValueWrap}>
          {field.href ? (
            <Link
              href={field.href}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              color="primary"
              underline="always"
              className={styles.previewFieldLink}
            >
              {fieldDisplayValue(field, edits)}
            </Link>
          ) : (
            <Text
              variant="bodyLg"
              tone="primary"
              casing="none"
              className={styles.previewFieldValue}
            >
              {fieldDisplayValue(field, edits)}
            </Text>
          )}
          {edit && (
            <IconButton
              title="Edit"
              size="small"
              aria-label={`Edit ${field.label}`}
              disabled={disabled}
              onClick={() => onToggle(edit.param)}
              className={styles.editIcon}
            >
              <Icon name="edit" size="sm" />
            </IconButton>
          )}
          {isEdited && (
            <IconButton
              title="Revert"
              size="small"
              aria-label={`Revert ${field.label} to original value`}
              disabled={disabled}
              onClick={() => onRevert(edit.param)}
              className={styles.editIcon}
            >
              <Icon name="undo" size="sm" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  )
}

type ApprovalCardProps = {
  approval: IApprovalSummary
  isApproving: boolean
  isRejecting: boolean
  onApprove: (approvalId: string, paramOverrides?: Record<string, unknown>) => void
  onReject: (approvalId: string) => void
}

// One approval card. Self-contained edit state — keyed by approval.id at the
// call site, so switching cards remounts and resets any in-progress edits.
const ApprovalCard = ({
  approval,
  isApproving,
  isRejecting,
  onApprove,
  onReject,
}: ApprovalCardProps) => {
  // Params whose inline input is currently open (toggled by the pencil).
  const [editingParams, setEditingParams] = useState<Set<string>>(new Set())
  // Only params the user actually touched; merged over stored params on accept.
  const [edits, setEdits] = useState<Record<string, string>>({})

  const preview = approval.preview
  const isResolving = isApproving || isRejecting
  const hasEdits = Object.keys(edits).length > 0

  const handleToggle = useCallback((param: string) => {
    setEditingParams((prev) => {
      const next = new Set(prev)
      if (next.has(param)) next.delete(param)
      else next.add(param)
      return next
    })
  }, [])

  const handleEdit = useCallback((param: string, value: string) => {
    setEdits((prev) => ({ ...prev, [param]: value }))
  }, [])

  const handleRevert = useCallback((param: string) => {
    setEdits((prev) => {
      const next = { ...prev }
      delete next[param]
      return next
    })
    setEditingParams((prev) => {
      const next = new Set(prev)
      next.delete(param)
      return next
    })
  }, [])

  const handleApprove = useCallback(() => {
    onApprove(approval.id, hasEdits ? edits : undefined)
  }, [onApprove, approval.id, hasEdits, edits])

  const approveLabel = hasEdits ? 'Accept with edits' : 'Approve'

  return (
    <Card withBorder className={styles.card}>
      <Box className={styles.header}>
        <Text variant="title">Approval needed</Text>
        <Chip
          size="small"
          label={humanizeServiceName(approval.serviceName)}
          className={styles.serviceChip}
        />
      </Box>

      {preview ? (
        <Box className={styles.previewBlock}>
          <Text variant="title" casing="none">
            {preview.title}
          </Text>
          {preview.subtitle && (
            <Text variant="body" tone="secondary" casing="none" className={styles.previewSubtitle}>
              {preview.subtitle}
            </Text>
          )}
          {preview.fields && preview.fields.length > 0 && (
            <Box className={styles.previewFields}>
              {preview.fields
                .filter(
                  (f) =>
                    !f.showWhen ||
                    fieldRawValue(f.showWhen.param, approval.params, edits) === f.showWhen.equals,
                )
                .map((f) => (
                  <FieldRow
                    key={f.label}
                    field={f}
                    params={approval.params}
                    edits={edits}
                    isEditing={f.edit ? editingParams.has(f.edit.param) : false}
                    disabled={isResolving}
                    onToggle={handleToggle}
                    onEdit={handleEdit}
                    onRevert={handleRevert}
                  />
                ))}
            </Box>
          )}
          {preview.warnings && preview.warnings.length > 0 && (
            <Box className={styles.previewWarnings}>
              {preview.warnings.map((w, i) => (
                <Text key={i} variant="body" tone="warning" casing="none">
                  {w}
                </Text>
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <>
          <Text variant="bodyLg" tone="secondary" className={styles.prompt}>
            {`${humanizeServiceName(approval.serviceName)} — review the details below before confirming.`}
          </Text>
          {renderRawParams(approval.params)}
        </>
      )}

      <Box className={styles.actions}>
        <Button
          secondary
          error
          disabled={isResolving}
          isLoading={isRejecting}
          onClick={() => onReject(approval.id)}
        >
          Reject
        </Button>
        <Button disabled={isResolving} isLoading={isApproving} onClick={handleApprove}>
          {approveLabel}
        </Button>
      </Box>
    </Card>
  )
}

export default ApprovalCard
